import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, ShieldCheck, Zap } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { productsAPI, ordersAPI, promotionsAPI, fmtError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';

// --- Product-type presentation metadata (rules copy is static per product type)
const TYPE_META = {
  one_step: {
    name: 'One-Step Program',
    tagline: 'Pay $20, pass a single-phase evaluation, unlock a funded account.',
    tag: '1-PHASE EVALUATION',
    phases: 1,
    rules: {
      leverage: '1:100',
      daily_dd: 4.0,
      max_dd: 8.0,
      min_days: 3,
      profit_target: 15.0,
    },
    priceLabel: 'Entry fee',
    priceHint: '$20 flat across every size · unlock fee paid after passing.',
  },
  instant: {
    name: 'Instant Funding',
    tagline: 'Skip the evaluation. Start directly on a simulated performance account.',
    tag: 'DIRECT PERFORMANCE',
    phases: 0,
    rules: {
      leverage: '1:100',
      daily_dd: 3.0,
      max_dd: 6.0,
      min_days: 3,
      profit_consistency_pct: 20.0,
    },
    priceLabel: 'Access fee',
    priceHint: '20% profit-consistency rule applies at each reward request.',
  },
};

function Stepper({ step }) {
  const steps = ['Program', 'Size', 'Rules', 'Checkout'];
  return (
    <div className="flex items-center gap-3 mb-10" data-testid="plans-stepper">
      {steps.map((s, i) => {
        const n = i + 1;
        const state = n < step ? 'done' : n === step ? 'active' : '';
        return (
          <div key={s} className="flex items-center gap-3">
            <div className={`step-dot ${state}`} />
            <span className={`label-sm ${n === step ? 'text-white' : ''}`}>{s}</span>
            {n < steps.length && <span className="h-px w-8 bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
}

export default function PlansWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const [products, setProducts] = useState([]);   // active products only
  const [currencies, setCurrencies] = useState([]);
  const [payCurrency, setPayCurrency] = useState('usdtbsc');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Promo code input (per session). We validate against the currently
  // selected product's applies_to bucket the moment the trader clicks "Apply".
  const [promoInput, setPromoInput] = useState('');
  const [promoResult, setPromoResult] = useState(null); // {valid, promo, error}
  const [promoBusy, setPromoBusy] = useState(false);

  const [step, setStep] = useState(() => {
    const s = Number(params.get('step') || '1');
    return Math.min(Math.max(s, 1), 4);
  });
  const [productType, setProductType] = useState(params.get('type') || '');
  const [size, setSize] = useState(params.get('size') || '');

  useEffect(() => {
    productsAPI.list().then(setProducts).catch(() => setProducts([]));
    ordersAPI.currencies().then(r => setCurrencies(r.currencies));
  }, []);

  // Group products by type → sizes list per type.
  const byType = useMemo(() => {
    const g = {};
    for (const p of products) {
      if (!g[p.product_type]) g[p.product_type] = [];
      g[p.product_type].push(p);
    }
    for (const t of Object.keys(g)) g[t].sort((a, b) => a.account_size - b.account_size);
    return g;
  }, [products]);

  const productTypes = useMemo(
    () => ['one_step', 'instant'].filter(t => (byType[t] || []).length > 0),
    [byType],
  );

  const currentProducts = byType[productType] || [];
  const currentMeta = TYPE_META[productType];
  const selectedProduct = useMemo(
    () => currentProducts.find(p => String(p.account_size) === String(size)),
    [currentProducts, size],
  );

  const goto = (n, extra = {}) => {
    const next = { step: String(n) };
    if (productType) next.type = productType;
    if (size) next.size = size;
    setParams({ ...next, ...extra });
    setStep(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSelectType = (t) => { setProductType(t); setSize(''); goto(2, { type: t }); };
  const onSelectSize = (s) => { setSize(String(s)); goto(3); };
  const toCheckout = () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/plans?step=4&type=${productType}&size=${size}`)}`);
      return;
    }
    goto(4);
  };

  const submitOrder = async () => {
    if (!selectedProduct) return;
    setError(''); setCreating(true);
    try {
      const code = promoResult?.valid ? promoInput.trim().toUpperCase() : undefined;
      const order = await productsAPI.createOrder(selectedProduct.product_id, payCurrency, code);
      nav(`/checkout/${order.order_id}`);
    } catch (e) { setError(fmtError(e)); }
    finally { setCreating(false); }
  };

  // ---- Promo helpers -------------------------------------------------------
  const promoAppliesTo = productType === 'one_step' ? 'one_step_entry'
                      : productType === 'instant'   ? 'instant_access'
                      : null;

  // Best available offer for the currently-viewed product (auto or code-gated).
  const activeOffer = useMemo(() => {
    if (!selectedProduct) return null;
    // Server-side auto-applied promo is already reflected via `promo` +
    // discounted `price_usd`. If the trader has typed a valid code, that
    // takes precedence for the summary math.
    if (promoResult?.valid) {
      return {
        code: promoResult.promo.code,
        percent: promoResult.promo.percent,
        applied: true,
      };
    }
    if (selectedProduct.promo && selectedProduct.original_price_usd) {
      return {
        code: selectedProduct.promo.code,
        percent: selectedProduct.promo.percent,
        applied: true,
      };
    }
    if (selectedProduct.promo_offer) {
      return {
        code: selectedProduct.promo_offer.code,
        percent: selectedProduct.promo_offer.percent,
        preview_price_usd: selectedProduct.promo_offer.preview_price_usd,
        applied: false,
      };
    }
    return null;
  }, [selectedProduct, promoResult]);

  // What the trader is billed right now, honouring the entered code first.
  const payableUsd = useMemo(() => {
    if (!selectedProduct) return 0;
    const base = Number(selectedProduct.original_price_usd ?? selectedProduct.price_usd ?? 0);
    if (promoResult?.valid) {
      const p = Number(promoResult.promo.percent) || 0;
      return Math.max(0, Math.ceil(base * (1 - p / 100) - 1e-9));
    }
    return Number(selectedProduct.price_usd ?? 0);
  }, [selectedProduct, promoResult]);

  const strikeUsd = useMemo(() => {
    if (!selectedProduct) return 0;
    return Number(selectedProduct.original_price_usd ?? selectedProduct.price_usd ?? 0);
  }, [selectedProduct]);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code || !promoAppliesTo) return;
    setPromoBusy(true);
    try {
      const r = await promotionsAPI.validate(code, promoAppliesTo);
      setPromoResult(r);
    } catch (e) {
      setPromoResult({ valid: false, error: 'Could not validate right now — try again.' });
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setPromoInput(''); setPromoResult(null); };

  // If the trader changes program type, the promo needs to re-validate on the new bucket.
  useEffect(() => { if (promoResult) setPromoResult(null); }, [productType]);

  const priceFrom = (t) => {
    const list = byType[t] || [];
    if (!list.length) return null;
    return Math.min(...list.map(p => p.price_usd || 0));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-14">
        {user?.is_terminated ? (
          <div className="card-tech rounded-lg p-10 sm:p-14 max-w-2xl mx-auto text-center border border-[#ff3366]/40" data-testid="plans-terminated-block">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#ff3366]/15 border border-[#ff3366]/40 flex items-center justify-center">
              <span className="text-[#ff3366] text-2xl font-bold">✕</span>
            </div>
            <div className="font-mono text-[10px] tracking-[3px] text-[#ff3366] mt-6">RISK &amp; COMPLIANCE</div>
            <h1 className="font-display text-4xl mt-2 text-[#ff3366]">Purchases disabled</h1>
            <p className="text-sm text-zinc-400 mt-4">
              Your Traders Spring Group account has been permanently terminated. You cannot purchase new evaluations.
              For questions, contact <a href="mailto:support@tradersspringfunding.com" className="text-[#ff3366] underline">support@tradersspringfunding.com</a>.
            </p>
            <Link to="/dashboard" className="btn-ghost btn-sm mt-8 inline-flex">Back to dashboard</Link>
          </div>
        ) : products.length === 0 ? (
          <div className="card-tech rounded-lg p-12 text-center max-w-2xl mx-auto" data-testid="plans-empty">
            <div className="label-sm mb-3">Programs</div>
            <h1 className="font-display text-3xl">Setup in progress</h1>
            <p className="text-sm text-zinc-400 mt-4">
              Our new One-Step and Instant Funding programs are being staged. Please check back shortly —
              or ask us in live chat and we'll notify you the moment they're live.
            </p>
          </div>
        ) : (
        <>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="label-sm mb-3">Start your evaluation</div>
            <h1 className="font-display text-4xl sm:text-5xl">Configure your program</h1>
          </div>
        </div>
        <Stepper step={step} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="step-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productTypes.map(t => {
                  const meta = TYPE_META[t];
                  const list = byType[t] || [];
                  const from = priceFrom(t);
                  return (
                    <button key={t} onClick={() => onSelectType(t)}
                            className={`card-tech rounded-lg p-8 text-left group transition ${productType === t ? 'border-[#00ff66]' : 'hover:border-white/30'}`}
                            data-testid={`choose-plan-${t}`}>
                      <div className="flex items-center justify-between">
                        <span className="label-xs">{meta.tag}</span>
                        <Zap size={14} className="text-[#00ff66]" />
                      </div>
                      <h3 className="font-display text-3xl mt-5">{meta.name}</h3>
                      <p className="text-sm text-zinc-500 mt-1">{meta.tagline}</p>
                      <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                        <div><div className="label-xs">LEVERAGE</div><div className="font-mono">{meta.rules.leverage}</div></div>
                        <div><div className="label-xs">MAX DD</div><div className="font-mono">{meta.rules.max_dd}%</div></div>
                        <div><div className="label-xs">DAILY DD</div><div className="font-mono">{meta.rules.daily_dd}%</div></div>
                        <div><div className="label-xs">MIN DAYS</div><div className="font-mono">{meta.rules.min_days}</div></div>
                      </div>
                      <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
                        <div>
                          <div className="label-xs">SIZES</div>
                          <div className="font-mono text-sm">
                            {list.map(p => `$${formatInt(p.account_size)}`).join(' · ')}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="label-xs">FROM</div>
                          <div className="font-mono text-lg">${formatInt(from || 0)}</div>
                        </div>
                      </div>
                      <div className="mt-4 text-[#00ff66] text-sm flex items-center gap-1">
                        Join Program <ArrowRight size={14} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 2 && currentMeta && (
            <motion.div key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="step-2">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-xs">SELECTED PROGRAM</div>
                  <div className="font-display text-2xl">{currentMeta.name}</div>
                </div>
                <button className="btn-ghost btn-sm" onClick={() => goto(1)} data-testid="back-to-plan">Change program</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentProducts.map(p => {
                  const hasUnlock = productType === 'one_step' && Number(p.unlock_fee_usd || 0) > 0;
                  return (
                    <button key={p.product_id} onClick={() => onSelectSize(p.account_size)}
                            className={`card-tech rounded-lg p-6 text-left ${String(size) === String(p.account_size) ? 'border-[#00ff66]' : 'hover:border-white/30'}`}
                            data-testid={`choose-size-${p.account_size}`}>
                      <div className="label-xs">SIMULATED ACCOUNT SIZE</div>
                      <div className="font-display text-3xl mt-1">${formatInt(p.account_size)}</div>
                      <div className="mt-6 flex items-end justify-between gap-3">
                        <div>
                          <div className="label-xs">{currentMeta.priceLabel}</div>
                          <div className="font-mono text-xl">${formatInt(p.price_usd)}</div>
                        </div>
                        {hasUnlock && (
                          <div className="text-right">
                            <div className="label-xs">UNLOCK FEE</div>
                            <div className="font-mono text-sm text-white">${formatInt(p.unlock_fee_usd)}</div>
                            <div className="text-[10px] text-zinc-500 mt-1">on 15% pass</div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 3 && currentMeta && selectedProduct && (
            <motion.div key="s3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="step-3">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 card-tech rounded-lg p-8">
                  <div className="label-sm">Program rules · {currentMeta.name}</div>
                  <h2 className="font-display text-3xl mt-2">${formatInt(selectedProduct.account_size)} Simulated Account</h2>
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    <Rule k="Leverage" v={currentMeta.rules.leverage} />
                    <Rule k="Daily drawdown" v={`${currentMeta.rules.daily_dd}%`} />
                    <Rule k="Max drawdown" v={`${currentMeta.rules.max_dd}%`} />
                    <Rule k="Minimum trading days" v={`${currentMeta.rules.min_days}`} />
                    {currentMeta.rules.profit_target != null && (
                      <Rule k="Profit objective" v={`${currentMeta.rules.profit_target}%`} />
                    )}
                    {currentMeta.rules.profit_consistency_pct != null && (
                      <Rule k="Profit consistency rule" v={`${currentMeta.rules.profit_consistency_pct}%`} />
                    )}
                    <Rule k="Platform" v="MetaTrader 5" />
                    <Rule k="Instruments" v="Synthetic Indices" />
                    <Rule k="Reward split" v="Up to 70%" />
                  </div>
                  <ul className="mt-8 space-y-2 text-sm text-zinc-400">
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> No time limit on evaluation.</li>
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> Weekend holding allowed.</li>
                    <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> EA and copy trading permitted within risk rules.</li>
                  </ul>
                </div>
                <aside className="card-tech rounded-lg p-6 h-fit sticky top-24">
                  <div className="label-sm">Order summary</div>
                  <Row k={currentMeta.name} v={`$${formatInt(selectedProduct.account_size)} simulated`} />
                  {promoResult?.valid && strikeUsd !== payableUsd ? (
                    <div className="flex items-center justify-between py-2" data-testid="summary-price-discounted">
                      <span className="text-sm text-zinc-400">{currentMeta.priceLabel}</span>
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-sm text-zinc-500 line-through">${formatInt(strikeUsd)}</span>
                        <span className="font-mono text-lg text-[#00ff66]">${formatInt(payableUsd)}</span>
                      </span>
                    </div>
                  ) : (
                    <Row k={currentMeta.priceLabel} v={`$${formatInt(selectedProduct.price_usd)}`} highlight />
                  )}
                  {productType === 'one_step' && Number(selectedProduct.unlock_fee_usd || 0) > 0 && (
                    <div className="mt-3 rounded-md border border-white/10 bg-white/[0.02] p-3">
                      <div className="label-xs">UNLOCK FEE (LATER)</div>
                      <div className="mt-1 text-lg font-mono text-white">${formatInt(selectedProduct.unlock_fee_usd)}</div>
                      <div className="text-xs text-zinc-500 mt-1">Paid after you pass the 15% objective, to activate your funded account.</div>
                    </div>
                  )}

                  {/* Promo code input */}
                  <PromoBlock
                    activeOffer={activeOffer}
                    promoInput={promoInput}
                    setPromoInput={setPromoInput}
                    promoResult={promoResult}
                    promoBusy={promoBusy}
                    applyPromo={applyPromo}
                    clearPromo={clearPromo}
                  />

                  <div className="my-4 h-px bg-white/5" />
                  <button className="btn-primary w-full justify-center" onClick={toCheckout} data-testid="continue-to-checkout">
                    Continue to checkout <ArrowRight size={16} />
                  </button>
                  <button className="btn-ghost btn-sm w-full mt-2" onClick={() => goto(2)} data-testid="back-to-size">Change size</button>
                </aside>
              </div>
            </motion.div>
          )}

          {step === 4 && currentMeta && selectedProduct && (
            <motion.div key="s4" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="step-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 card-tech rounded-lg p-8">
                  <div className="label-sm">Checkout</div>
                  <h2 className="font-display text-3xl mt-2">{currentMeta.name} · Access</h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    Access fee for participation in the Traders Spring Group evaluation program. Choose a currency below — we'll generate a payment address via NowPayments.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
                    {currencies.map(c => (
                      <button key={c.code} onClick={() => setPayCurrency(c.code)}
                              className={`card-tech rounded-lg p-4 text-left ${payCurrency === c.code ? 'border-[#00ff66]' : 'hover:border-white/30'}`}
                              data-testid={`pay-currency-${c.code}`}>
                        <div className="label-xs">{c.network}</div>
                        <div className="font-display text-lg mt-1">{c.name}</div>
                        <div className="font-mono text-xs text-zinc-500">{c.code.toUpperCase()}</div>
                      </button>
                    ))}
                  </div>
                  {error && <div className="mt-4 text-sm text-[#ff3366]" data-testid="checkout-error">{error}</div>}
                </div>
                <aside className="card-tech rounded-lg p-6 h-fit sticky top-24">
                  <div className="label-sm">Summary</div>
                  <Row k={currentMeta.name} v={`$${formatInt(selectedProduct.account_size)}`} />
                  {promoResult?.valid && strikeUsd !== payableUsd ? (
                    <div className="flex items-center justify-between py-2" data-testid="checkout-price-discounted">
                      <span className="text-sm text-zinc-400">{currentMeta.priceLabel} (USD)</span>
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-sm text-zinc-500 line-through">${formatInt(strikeUsd)}</span>
                        <span className="font-mono text-lg text-[#00ff66]">${formatInt(payableUsd)}</span>
                      </span>
                    </div>
                  ) : (
                    <Row k={`${currentMeta.priceLabel} (USD)`} v={`$${formatInt(selectedProduct.price_usd)}`} highlight />
                  )}
                  <Row k="Pay with" v={payCurrency.toUpperCase()} />

                  <PromoBlock
                    activeOffer={activeOffer}
                    promoInput={promoInput}
                    setPromoInput={setPromoInput}
                    promoResult={promoResult}
                    promoBusy={promoBusy}
                    applyPromo={applyPromo}
                    clearPromo={clearPromo}
                  />

                  <div className="my-4 h-px bg-white/5" />
                  <button disabled={creating} className="btn-primary w-full justify-center" onClick={submitOrder} data-testid="place-order">
                    {creating ? 'Creating invoice…' : (<>Start Evaluation <ArrowRight size={16} /></>)}
                  </button>
                  <div className="mt-3 text-[11px] text-zinc-500 flex items-start gap-2">
                    <ShieldCheck size={14} className="text-[#00ff66] mt-0.5" />
                    Payments processed securely by NowPayments. Your simulated trading credentials are delivered by email after confirmation.
                  </div>
                </aside>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Rule({ k, v }) {
  return (
    <div>
      <div className="label-xs">{k}</div>
      <div className="font-mono text-lg mt-1">{v}</div>
    </div>
  );
}
function Row({ k, v, highlight, hint }) {
  return (
    <div className={`flex items-center justify-between py-2 ${hint ? 'opacity-70' : ''}`}>
      <span className="text-sm text-zinc-400">{k}</span>
      <span className={`font-mono ${highlight ? 'text-[#00ff66] text-lg' : 'text-white text-sm'}`}>{v}</span>
    </div>
  );
}

/**
 * Promo code redemption block used on both the Rules-summary sidebar and the
 * Checkout sidebar. Renders differently in three states:
 *   1. Trader hasn't tried a code yet — show the input + "Apply" button,
 *      plus a hint if the product has a promo_offer teaser (e.g. TSGBACK).
 *   2. Code valid — green "Applied" chip + percent + code with a "remove" X.
 *   3. Code invalid — red error text; input stays with typed value.
 */
function PromoBlock({ activeOffer, promoInput, setPromoInput, promoResult, promoBusy, applyPromo, clearPromo }) {
  const applied = promoResult?.valid;
  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="promo-block">
      <div className="label-xs">PROMO CODE</div>
      {applied ? (
        <div className="mt-2 flex items-center justify-between gap-3" data-testid="promo-applied">
          <div>
            <div className="font-mono text-sm text-[#00ff66] tracking-wider">{promoResult.promo.code}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {promoResult.promo.percent}% off applied
            </div>
          </div>
          <button
            onClick={clearPromo}
            className="text-[11px] font-mono tracking-wider text-zinc-500 hover:text-white transition"
            data-testid="promo-remove-btn"
          >
            REMOVE
          </button>
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-stretch gap-2">
            <input
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="flex-1 rounded-md bg-black/50 border border-white/10 px-3 py-2 text-sm font-mono tracking-wider text-white placeholder:text-zinc-600 focus:border-[#00ff66]/60 focus:outline-none"
              maxLength={24}
              data-testid="promo-code-input"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
            />
            <button
              onClick={applyPromo}
              disabled={promoBusy || !promoInput.trim()}
              className="btn-ghost btn-sm shrink-0"
              data-testid="promo-apply-btn"
            >
              {promoBusy ? '…' : 'Apply'}
            </button>
          </div>
          {promoResult?.error && (
            <div className="mt-2 text-[11px] text-[#ff3366]" data-testid="promo-error">
              {promoResult.error}
            </div>
          )}
          {!promoResult && activeOffer && !activeOffer.applied && (
            <button
              onClick={() => setPromoInput(activeOffer.code)}
              className="mt-2 text-[11px] font-mono tracking-wider text-[#00ff66] hover:underline"
              data-testid="promo-hint"
            >
              Use code {activeOffer.code} · {activeOffer.percent}% OFF
            </button>
          )}
        </>
      )}
    </div>
  );
}
